import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { type FieldErrorDto, type OptionDto } from "../../../../../../autogen/bff-api";
import { useDisplayDate } from "../../../../../../common/formatting/useDisplayDate";
import { refusedFieldErrors } from "../../../../../../common/redux/reducers/contractReducer";
import { type EditContractCommandType } from "../../../../../../common/redux/thunks/contract/edit-contract-thunk";
import { useExtensionName } from "../../../../options/extensionName";
import { useContractState } from "../../../useContractState";

const END_DATE_PAST_LAST = "zz_FieldErrorCode_EndDateAfterLastSupportedDate";
const EXTENSION_PAST_LAST = "zz_FieldErrorCode_ExtensionEndsAfterLastSupportedDate";

export const useLastSupportedDateExplanation = (): ((refusals: FieldErrorDto[]) => string | null) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const options = useContractState().contract.dataFields.options;
  const extensionName = useExtensionName(options ?? []);

  return useCallback(
    (refusals: FieldErrorDto[]) => {
      const endDateRefusal = refusals.find((refusal) => refusal.code === END_DATE_PAST_LAST);
      if (endDateRefusal) {
        return t("The end date can be no later than {{date}}.", {
          date: displayDate(endDateRefusal.params?.lastDate ?? ""),
        });
      }
      const extensionRefusals = refusals.filter((refusal) => refusal.code === EXTENSION_PAST_LAST);
      if (extensionRefusals.length === 0) return null;
      const names = extensionRefusals.map(
        ({ params }) =>
          params?.name?.trim() ||
          extensionName(
            (options ?? []).find((option) => option.id === params?.optionId) ?? ({ id: params?.optionId } as OptionDto)
          )
      );
      return t("{{names}} would then end after {{date}}, the last date a contract can run to.", {
        names: names.join(", "),
        date: displayDate(extensionRefusals[0].params?.lastDate ?? ""),
      });
    },
    [t, displayDate, options, extensionName]
  );
};

export const useLastSupportedDateRefusal = (type: EditContractCommandType): string | null => {
  const explain = useLastSupportedDateExplanation();
  return explain(refusedFieldErrors(useContractState(), type));
};
